'use client';

import Link from 'next/link';
import OptimizedImage from './OptimizedImage';
import Search from './Search';
import ThemeToggle from './ThemeToggle';
import SavedContentMenu from './SavedContentMenu';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/components/ThemeProvider';

// Root tab routes where back button should not appear
const ROOT_TAB_ROUTES = ['/', '/daily-sync', '/tools', '/insights', '/dashboard'];

function getPageTitle(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return '';
  // Use the last meaningful segment as the title
  const last = segments[segments.length - 1];
  return last
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function Header() {
  const { theme } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, login, logout, isLoading } = useAuth();
  const [loginLoading, setLoginLoading] = useState<'login' | 'signup' | null>(null);
  const [native, setNative] = useState(false);
  const [iosNative, setIosNative] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

  // Determine if we should show back button on native
  const showNativeBack = useMemo(() => {
    if (!native) return false;
    return !ROOT_TAB_ROUTES.includes(pathname);
  }, [native, pathname]);

  const nativePageTitle = useMemo(() => {
    if (!showNativeBack) return '';
    return getPageTitle(pathname);
  }, [showNativeBack, pathname]);

  // Detect native platform
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) {
      setNative(true);
      if (cap.getPlatform?.() === 'ios') {
        setIosNative(true);
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking the mobile menu button itself
      if (mobileMenuButtonRef.current && mobileMenuButtonRef.current.contains(event.target as Node)) {
        return;
      }

      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobileMenuOpen]);

  // Android hardware back button — close menu or navigate back
  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);
  useEffect(() => {
    if (!native) return;

    let cleanup: (() => void) | undefined;
    (async () => {
      try {
        const { App: CapApp } = await import('@capacitor/app');
        const listener = await CapApp.addListener('backButton', () => {
          if (isMobileMenuOpen) {
            closeMobileMenu();
          } else if (isDropdownOpen) {
            setIsDropdownOpen(false);
          } else if (window.history.length > 1) {
            window.history.back();
          }
        });
        cleanup = () => listener.remove();
      } catch {
        // @capacitor/app not available
      }
    })();

    return () => { cleanup?.(); };
  }, [native, isMobileMenuOpen, isDropdownOpen, closeMobileMenu]);

  // Close mobile menu on window resize to desktop (debounced for better INP)
  useEffect(() => {
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= 1024) { // lg breakpoint
          setIsMobileMenuOpen(false);
        }
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  return (
    <header
      style={{
        backgroundColor: theme === 'dark' ? '#0A1E2E' : 'white',
        borderBottomColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.2)' : '#e5e7eb'
      }}
      className="border-b"
    >
      <nav className={`container mx-auto px-4 ${native ? 'py-3' : 'py-6'}`}>
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group shrink-0">
            <OptimizedImage
              src="/logos/logo_header.webp"
              alt="The Art of CTO Logo"
              width={50}
              height={50}
              className="transition-transform group-hover:scale-105"
            />
            <span style={{ color: theme === 'dark' ? '#F5EBD7' : '#111827' }} className="text-lg md:text-xl xl:text-2xl font-bold group-hover:text-accent transition-colors hidden md:block whitespace-nowrap">
              The Art of CTO
            </span>
          </Link>

          {/* Search - Desktop (hidden on native — search is in tab content) */}
          {!native && (
            <div className="flex-1 max-w-md hidden lg:block">
              <Search />
            </div>
          )}

          {/* Mobile Menu Button — 44px minimum touch target per iOS HIG */}
          {!native && <button
            ref={mobileMenuButtonRef}
            className="lg:hidden p-3 -mr-1 hover:text-accent active:text-accent transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>}

          {/* Native: back button + page title + search/theme */}
          {native && (
            <div className="flex items-center gap-3 flex-1 justify-end">
              {showNativeBack && (
                <>
                  <button
                    onClick={() => router.back()}
                    className="p-2 -ml-2 text-accent active:text-accent/70 transition-colors"
                    aria-label="Go back"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm font-semibold text-foreground truncate flex-1 text-center">
                    {nativePageTitle}
                  </span>
                </>
              )}
              {!showNativeBack && <div className="flex-1" />}
              <Search />
              <ThemeToggle />
            </div>
          )}

          {/* Desktop Navigation (hidden on native — tab bar replaces it) */}
          {!native && <ul className="hidden lg:flex gap-6 items-center">
            <li
              className="relative"
              ref={dropdownRef}
            >
              <button
                ref={buttonRef}
                className="hover:text-accent transition-colors"
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
                aria-controls="resources-dropdown"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                onKeyDown={handleKeyDown}
              >
                Resources ▾
              </button>

              {/* Dropdown Menu - keyboard and mouse accessible */}
              {isDropdownOpen && (
                <div
                  id="resources-dropdown"
                  role="menu"
                  aria-label="Resources menu"
                  className="absolute top-full right-0 pt-2 z-50"
                >
                  <div
                    style={{
                      backgroundColor: theme === 'dark' ? '#0A1E2E' : 'white',
                      borderColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.2)' : '#e5e7eb'
                    }}
                    className="w-56 border rounded-lg shadow-xl py-2"
                  >
                    <div className="px-4 py-2">
                      {/* Dashboard Link for Authenticated Users */}
                      {isAuthenticated && (
                        <>
                          <Link
                            href="/dashboard"
                            role="menuitem"
                            className="flex items-center gap-2 px-2 py-2 mb-2 text-accent hover:text-accent transition-colors text-sm font-semibold bg-accent-muted rounded-lg"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                            My Dashboard
                          </Link>
                          <div style={{ borderColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.1)' : '#e5e7eb' }} className="border-t my-2" />
                        </>
                      )}

                      <p style={{ color: theme === 'dark' ? 'rgba(232, 152, 65, 0.6)' : '#9ca3af' }} className="text-xs uppercase tracking-wider mb-2">Learn</p>
                      <ul className="space-y-1 mb-3" role="group">
                        <li role="none">
                          <Link
                            href="/daily-sync"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Daily Sync
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/insights"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Insights
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/frameworks"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Frameworks
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/playbooks"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Playbooks
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/comparisons"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Comparisons
                          </Link>
                        </li>
                      </ul>

                      <div style={{ borderColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.1)' : '#e5e7eb' }} className="border-t my-2" />

                      <p style={{ color: theme === 'dark' ? 'rgba(232, 152, 65, 0.6)' : '#9ca3af' }} className="text-xs uppercase tracking-wider mb-2">Tools</p>
                      <ul className="space-y-1 mb-3" role="group">
                        <li role="none">
                          <Link
                            href="/tools"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm font-semibold"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            All Tools →
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/calculators"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Calculators
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/tech-radar"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Tech Radar
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/outages"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Outage Tracker
                          </Link>
                        </li>
                      </ul>

                      <div style={{ borderColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.1)' : '#e5e7eb' }} className="border-t my-2" />

                      <p style={{ color: theme === 'dark' ? 'rgba(232, 152, 65, 0.6)' : '#9ca3af' }} className="text-xs uppercase tracking-wider mb-2">Reference</p>
                      <ul className="space-y-1 mb-3" role="group">
                        <li role="none">
                          <Link
                            href="/templates"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Templates
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/architectures"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Architectures
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/hiring"
                            role="menuitem"
                            style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                            className="block px-2 py-1 hover:text-accent transition-colors text-sm"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Interview Questions
                          </Link>
                        </li>
                        <li role="none">
                          <Link
                            href="/frameworks/premium"
                            role="menuitem"
                            className="block px-2 py-1 text-accent hover:text-accent transition-colors text-sm font-semibold flex items-center gap-1"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                            Premium
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </li>
            <li>
              <Link href="/cto-office" className="hover:text-accent transition-colors font-semibold text-accent">
                Fractional CTO
              </Link>
            </li>
            {/* Hide pricing on iOS native (Apple Guideline 3.1.1) */}
            {!iosNative && (
              <li>
                <Link href="/pricing" className="hover:text-accent transition-colors">
                  Pricing
                </Link>
              </li>
            )}

            {/* Dashboard & Command Center Links - Authenticated Users */}
            {isAuthenticated && (
              <>
                <li>
                  <Link
                    href="/dashboard"
                    className="hover:text-accent transition-colors flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    Dashboard
                  </Link>
                </li>
                <li>
                  <Link
                    href="/command-center"
                    className="hover:text-accent transition-colors flex items-center gap-1.5"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                    </svg>
                    Command Center
                  </Link>
                </li>
              </>
            )}

            {/* Saved Content Menu */}
            <li>
              <SavedContentMenu />
            </li>

            {/* Theme Toggle */}
            <li>
              <ThemeToggle />
            </li>

            {/* Auth Buttons */}
            <li>
              {isLoading ? (
                <div className="w-20 h-9 bg-surface rounded animate-pulse" />
              ) : isAuthenticated ? (
                <div className="flex items-center gap-3">
                  {user?.picture && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={user.picture}
                      alt={user.name || 'User'}
                      className="w-8 h-8 rounded-full border border-border"
                    />
                  )}
                  <button
                    onClick={() => logout()}
                    className="text-sm px-3 py-1.5 bg-accent-muted hover:bg-accent-muted text-accent rounded transition-colors"
                  >
                    Log Out
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setLoginLoading('login');
                      login({ screenHint: 'login' });
                    }}
                    disabled={loginLoading !== null}
                    className="text-sm px-3 py-1.5 border border-border hover:bg-accent-muted text-accent rounded transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-wait flex items-center gap-2"
                  >
                    {loginLoading === 'login' && (
                      <div className="w-3 h-3 border-2 border-border border-t-orange rounded-full animate-spin" />
                    )}
                    Log In
                  </button>
                  <button
                    onClick={() => {
                      setLoginLoading('signup');
                      login({ screenHint: 'signup' });
                    }}
                    disabled={loginLoading !== null}
                    className="text-sm px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground rounded font-semibold transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-wait flex items-center gap-2"
                  >
                    {loginLoading === 'signup' && (
                      <div className="w-3 h-3 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
                    )}
                    Sign Up Free
                  </button>
                </div>
              )}
            </li>
          </ul>}
        </div>

        {/* Mobile Search - Full Width Below Nav (hidden on native) */}
        {!native && (
          <div className="mt-4 lg:hidden">
            <Search />
          </div>
        )}

        {/* Mobile Menu (hidden on native — tab bar replaces it) */}
        {!native && isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            style={{
              backgroundColor: theme === 'dark' ? '#0A1E2E' : 'white',
              borderTopColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.2)' : '#e5e7eb'
            }}
            className="lg:hidden mt-4 pb-4 border-t pt-4"
          >
            <ul className="space-y-1">
              {/* Featured CTA */}
              <li className="pb-3">
                <Link
                  href="/cto-office"
                  className="block py-3 px-4 bg-accent-muted rounded-lg text-accent hover:bg-accent-muted transition-colors font-semibold text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Fractional CTO Services
                </Link>
              </li>

              {/* Learn Section */}
              <li className="pt-2">
                <p style={{ color: theme === 'dark' ? 'rgba(232, 152, 65, 0.6)' : '#9ca3af' }} className="text-xs uppercase tracking-wider px-2 mb-2">Learn</p>
                <ul className="space-y-1">
                  <li>
                    <Link
                      href="/daily-sync"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Daily Sync
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/insights"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Insights
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/frameworks"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Frameworks
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/playbooks"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Playbooks
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/comparisons"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Comparisons
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Tools Section */}
              <li className="pt-3">
                <p style={{ color: theme === 'dark' ? 'rgba(232, 152, 65, 0.6)' : '#9ca3af' }} className="text-xs uppercase tracking-wider px-2 mb-2">Tools</p>
                <ul className="space-y-1">
                  <li>
                    <Link
                      href="/tools"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors font-semibold"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      All Tools →
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/calculators"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Calculators
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/tech-radar"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Tech Radar
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/outages"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Outage Tracker
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Reference Section */}
              <li className="pt-3">
                <p style={{ color: theme === 'dark' ? 'rgba(232, 152, 65, 0.6)' : '#9ca3af' }} className="text-xs uppercase tracking-wider px-2 mb-2">Reference</p>
                <ul className="space-y-1">
                  <li>
                    <Link
                      href="/templates"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Templates
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/architectures"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Architectures
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/hiring"
                      style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                      className="block py-2 px-2 hover:text-accent transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Interview Questions
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/frameworks/premium"
                      className="block py-2 px-2 text-accent hover:text-accent transition-colors font-semibold flex items-center gap-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      Premium Content
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Other Links */}
              <li className="pt-3" style={{ borderColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.1)' : '#e5e7eb' }}>
                <div className="border-t pt-3">
                  <ul className="space-y-1">
                    {/* Hide pricing on iOS native (Apple Guideline 3.1.1) */}
                    {!iosNative && (
                      <li>
                        <Link
                          href="/pricing"
                          style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                          className="block py-2 px-2 hover:text-accent transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Pricing
                        </Link>
                      </li>
                    )}
                    {/* Hide newsletter signup on native — push notifications replace it */}
                    {!native && (
                      <li>
                        <Link
                          href="/newsletter"
                          style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                          className="block py-2 px-2 hover:text-accent transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Newsletter
                        </Link>
                      </li>
                    )}
                    <li>
                      <Link
                        href="/about"
                        style={{ color: theme === 'dark' ? '#F5EBD7' : '#374151' }}
                        className="block py-2 px-2 hover:text-accent transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        About
                      </Link>
                    </li>
                  </ul>
                </div>
              </li>

              {/* Authenticated User Section */}
              {isAuthenticated && (
                <li className="pt-3" style={{ borderColor: theme === 'dark' ? 'rgba(232, 152, 65, 0.1)' : '#e5e7eb' }}>
                  <div className="border-t pt-3">
                    <p style={{ color: theme === 'dark' ? 'rgba(232, 152, 65, 0.6)' : '#9ca3af' }} className="text-xs uppercase tracking-wider px-2 mb-2">Your Account</p>
                    <ul className="space-y-1">
                      <li>
                        <Link
                          href="/dashboard"
                          className="block py-2 px-2 hover:text-accent transition-colors flex items-center gap-2"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                          </svg>
                          Dashboard
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/command-center"
                          className="block py-2 px-2 hover:text-accent transition-colors flex items-center gap-2"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                          </svg>
                          Command Center
                        </Link>
                      </li>
                    </ul>
                  </div>
                </li>
              )}

              {/* Auth Buttons */}
              <li className="pt-2 border-t border-border">
                {isLoading ? (
                  <div className="w-full h-9 bg-surface rounded animate-pulse" />
                ) : isAuthenticated ? (
                  <div className="flex items-center gap-3 py-2">
                    {user?.picture && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={user.picture}
                        alt={user.name || 'User'}
                        className="w-8 h-8 rounded-full border border-border"
                      />
                    )}
                    <button
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="text-sm px-3 py-1.5 bg-accent-muted hover:bg-accent-muted text-accent rounded transition-colors"
                    >
                      Log Out
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setLoginLoading('signup');
                        login({ screenHint: 'signup' });
                        setIsMobileMenuOpen(false);
                      }}
                      disabled={loginLoading !== null}
                      className="w-full text-sm px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground rounded font-semibold transition-colors cursor-pointer disabled:opacity-70 disabled:cursor-wait flex items-center justify-center gap-2"
                    >
                      {loginLoading === 'signup' && (
                        <div className="w-3 h-3 border-2 border-navy/30 border-t-navy rounded-full animate-spin" />
                      )}
                      Sign Up Free
                    </button>
                    <button
                      onClick={() => {
                        setLoginLoading('login');
                        login({ screenHint: 'login' });
                        setIsMobileMenuOpen(false);
                      }}
                      disabled={loginLoading !== null}
                      className="w-full text-sm px-4 py-2 border border-border hover:bg-accent-muted text-accent rounded transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-2"
                    >
                      {loginLoading === 'login' && (
                        <div className="w-3 h-3 border-2 border-border border-t-orange rounded-full animate-spin" />
                      )}
                      Log In
                    </button>
                  </div>
                )}
              </li>
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
}
